<script lang="ts">
	import { ControlsContainer, ListItem, Scroll, Stack, Text } from '@Shelly/shelly-design-system';
	import { capFirstChar } from 'src/helpers/utilities';
	import { createEventDispatcher, onDestroy, onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	import { getEventBasedOnIcon, isMotionIcon } from 'src/helpers/device_data_helpers';
	import { getEvents, getTagsAndDeviceChannel, parseEventLogData } from '../EventLog/event_log_helper';
	import { isMotion } from 'src/helpers/interface_helpers';
	import { eventLogStore } from 'src/stores/features/event-log';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	let className = 'mb-s';

	export { className as class };
	export let device;

	let lastEvents = [];

	let typeWithIconInData =
		device?.type === 'SBDW-002C' ||
		device?.type === 'SBDW-103C' ||
		device?.type === 'SBDW-002D' ||
		device?.type === 'SBMO-003Z' ||
		device?.type === 'SBMO-103Z' ||
		device?.type === 'SHMOS-01' ||
		device?.type === 'SHMOS-02' ||
		device?.type === 'SNSN-0031Z' ||
		device?.type === 'S3SN-0082A' ||
		device?.type === 'S4SN-0031Z' ||
		device?.type === 'S4SN-0U61X';

	let typeWithIconInEvent =
		device?.type === 'SHDW-2' || device?.type === 'SHDW-1' || device?.type === 'S4SN-0U61X';

	const getAllEventsForDevice = async () => {
		const [tags, deviceChannel] = getTagsAndDeviceChannel(device, []);
		const res = await getEvents(false, null, tags, 5);
		let fetchedEvents = await parseEventLogData(res, deviceChannel).slice(0, 4);
		// Sort merged events by raw timestamp in descending order
		fetchedEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
		// Limit to 4 events
		lastEvents = fetchedEvents.slice(0, 4);
		// Format each event
		lastEvents = lastEvents.map((event) => {
			const date = new Date(event.timestamp);
			const year = date.getFullYear();
			const currentYear = new Date().getFullYear();
			const formattedDate = date.toLocaleString('en-GB', {
				day: '2-digit',
				month: 'short',
				...(year !== currentYear && { year: 'numeric' }),
				hour: '2-digit',
				minute: '2-digit',
				hour12: false,
			});
			const eventIcon = typeWithIconInData && event.icon?.icon;
			const stateIcon = typeWithIconInEvent && event.data?.state?.icon;
			const doorIcon = typeWithIconInEvent && event.data?.door?.icon;
			const icon = eventIcon || stateIcon || doorIcon;
			return {
				...event,
				icon: isMotionIcon(icon),
				title:
					capFirstChar(getEventBasedOnIcon(icon)) ||
					capFirstChar(event?.data?.state?.value) ||
					capFirstChar(event?.data?.door?.value),
				timestamp: formattedDate,
				originalTimestamp: event.timestamp,
			};
		});
	};

	onMount(() => {
		getAllEventsForDevice();
		eventLogStore.deviceId = device.id;
	});

	onDestroy(() => {
		eventLogStore.clear();
		unsubscribe();
	});

	const unsubscribe = eventLogStore.getAllEvents(async (newEvents) => {
		// Truth be told, this is only to trigger the refresh for the events, since
		// we do not need to append anything
		getAllEventsForDevice();
	});

	$: noLastEventsData = lastEvents.some((event) => !event.icon || !event.title);

	const dispatch = createEventDispatcher();
</script>

{#if lastEvents?.length && (typeWithIconInEvent || typeWithIconInData) && !noLastEventsData && isMotion(device.type)}
	{#if $showNewLayout}
		<div class="mb-m">
			<Text
				element="p"
				styleType="label-large"
				class="mb-s text-uppercase text-color-muted text-center"
			>
				{$format('last_events_heading', {
					default: 'Last events',
				})}
			</Text>
			<Scroll smallerScroll>
				<Stack gap="xs">
					{#each lastEvents as listItem}
						<ListItem
							fontWeight="solid"
							viewType="fullColored"
							listItemBgColor="--color-0"
							icon={listItem?.icon}
							title={listItem?.title}
							actionBtn
							on:click={() => {
								dispatch('click', listItem);
							}}
							progress={{
								percent: 0,
								title: listItem?.timestamp,
								showPercentBar: false,
							}}
						/>
					{/each}
				</Stack>
			</Scroll>
		</div>
	{:else}
		<ControlsContainer
			smallerScroll
			class={className}
			size="s"
			color={$showNewLayout ? 'color-0' : undefined}
		>
			<svelte:fragment slot="first">
				<Text element="p" class="text-bold mb-s">
					{capFirstChar(
						$format('last_events_heading', {
							default: 'Last events',
						}),
					)}
				</Text>
				<Scroll smallerScroll>
					<Stack gap="xs">
						{#each lastEvents as listItem}
							<ListItem
								fontWeight="solid"
								viewType="fullColored"
								listItemBgColor="--color-0"
								icon={listItem?.icon}
								title={listItem?.title}
								actionBtn
								on:click={() => {
									dispatch('click', listItem);
								}}
								progress={{
									percent: 0,
									title: listItem?.timestamp,
									showPercentBar: false,
								}}
							/>
						{/each}
					</Stack>
				</Scroll>
			</svelte:fragment>
		</ControlsContainer>
	{/if}
{/if}
